import { Button, IconButton, Stack, Typography } from '@mui/material'
import { pipe, toPairs } from 'ramda'
import { useEffect } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { Case, Switch, When } from 'react-if'
import { useNavigate, useParams } from 'react-router-dom'

import {
  Icon,
  useConfirm,
  VailAccordion,
  VailSelect,
  VailSwitch,
  VailTextField,
} from '@/components'
import LanguageSelector from '@/components/LanguageSelector'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useOrg, useUrlParams } from '@/hooks'
import WorkshopTitlePreview from '@/screens/ComponentLibrary/components/WorkshopTitlePreview'
import {
  useCreateCustomComponent,
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { safeCypressID } from '@/utils'
import { canUseName } from '@/utils'
import { ComponentType } from '@/utils/api'

import {
  IntroductionSettings,
  SupportedLocales,
} from '../../types/customComponents'
export type SettingsForm = {
  settings: IntroductionSettings
  name: string
  description: string
}
export const SettingsTab = () => {
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()
  const org = useOrg(orgID)
  const [{ lang = 'en' }] = useUrlParams()
  const languagesSupported = org.data?.languagesSupported || ['en']

  const form = useForm<SettingsForm>({
    defaultValues: {
      description: '',
      name: '',
      settings: {
        IncludeTermsAgreement: false,
        IntroButtonText: null,
        includeIntroButton: false,
      },
    },
  })
  const navigate = useNavigate()

  const createIntro = useCreateCustomComponent<IntroductionSettings>(orgID)
  const currentIntro = useGetCustomComponent<IntroductionSettings>(componentID)
  const updateIntro =
    useUpdateCustomComponent<IntroductionSettings>(componentID)
  const { reset } = form

  const onChangeSwitch =
    (registerField: keyof IntroductionSettings) => (open: boolean) => {
      if (open) form.register(`settings.${registerField}`)
      else form.unregister(`settings.${registerField}`)
    }

  const onChangeIntroButton = (open: boolean) => {
    if (open) {
      form.setValue('settings.IntroButtonText.en', "Let's get started")
      return
    }
    form.setValue('settings.IntroButtonText', null)
  }

  useEffect(() => {
    if (!currentIntro.data) return
    const { name, description, settings } = currentIntro.data
    const localizeString =
      (pathToReplace: (string | number)[]) =>
      (str: keyof IntroductionSettings) =>
      (obj: IntroductionSettings): IntroductionSettings => {
        if (typeof obj?.[str] === 'string')
          return {
            ...obj,
            [str]: {
              en: obj?.[str]?.split('\n').filter(Boolean) || [],
            },
          }
        return obj
      }

    const localizeStringArray = localizeString(['en', 0])
    const localizeSingleString = localizeString(['en'])

    const updateSettings = pipe<
      [IntroductionSettings],
      IntroductionSettings,
      IntroductionSettings,
      IntroductionSettings,
      IntroductionSettings
    >(
      localizeStringArray('IntroText'),
      localizeStringArray('ConsentText'),
      localizeSingleString('IntroButtonText'),
      localizeSingleString('ConsentButtonText')
    )

    reset({ description, name, settings: updateSettings(settings) })
  }, [currentIntro.data, reset])

  const onSubmit = form.handleSubmit(async (data) => {
    if (componentID)
      return await updateIntro.mutateAsync({
        ...data,
        id: componentID,
        type: ComponentType.introduction,
      })
    const res = await createIntro.mutateAsync({
      ...data,
      type: ComponentType.introduction,
    })
    return navigate(`../${res?.id}`)
  })

  return (
    <FormProvider {...form}>
      <Stack
        component="form"
        height="100%"
        justifyContent="start"
        rowGap={2}
        onSubmit={onSubmit}
      >
        <Button
          id={safeCypressID('save Introduction')}
          sx={{ alignSelf: 'flex-end', mb: 2 }}
          type="submit"
          variant="contained"
        >
          Save
        </Button>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: ['column', null, 'row'],
            gap: 2,
          }}
          title="Description"
        >
          <Stack flex={1} gap={2}>
            <VailTextField
              control={form.control}
              label="Name"
              name="name"
              rules={{
                validate: {
                  canUseName: async (data) =>
                    canUseName(orgID, componentID, data),
                },
              }}
            />
            <WorkshopTitlePreview name={form.watch('name')} />
            <VailSelect
              disabled
              defaultValue={ComponentType.introduction}
              label="Type"
              name="type"
              options={[
                {
                  label: 'Introduction',
                  value: ComponentType.introduction,
                },
              ]}
            />
          </Stack>
          <Stack flex={1}>
            <VailTextField
              multiline
              control={form.control}
              label="Description"
              maxRows={5}
              minRows={5}
              name="description"
              rules={{ required: false }}
            />
          </Stack>
        </VailAccordion>
        <VailAccordion defaultExpanded useDefaultBox title="Configuration">
          <LanguageSelector />
          <Stack direction="row" gap={2}>
            <Stack sx={{ flex: 1, gap: 2 }}>
              <Typography variant="h6">Introduction</Typography>
              <Switch>
                {languagesSupported.map((orgLang) => (
                  <Case key={orgLang} condition={orgLang === lang}>
                    <ArrayText
                      label="Intro Text"
                      name={`settings.IntroText.${lang}`}
                    />
                  </Case>
                ))}
              </Switch>
              <VailSwitch
                control={form.control}
                label="Include intro button"
                name="settings.includeIntroButton"
                onChange={onChangeIntroButton}
              />
              <When condition={!!form.watch('settings.includeIntroButton')}>
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={orgLang === lang}>
                      <VailTextField
                        control={form.control}
                        label="Button Text"
                        name={`settings.IntroButtonText.${
                          orgLang as SupportedLocales
                        }`}
                      />
                    </Case>
                  ))}
                </Switch>
              </When>
            </Stack>

            <Stack sx={{ flex: 1, gap: 2 }}>
              <Typography variant="h6">Terms Agreement</Typography>
              <VailSwitch
                control={form.control}
                label="Include terms agreement"
                name="settings.IncludeTermsAgreement"
                onChange={onChangeSwitch('IncludeTermsAgreement')}
              />

              <When condition={!!form.watch('settings.IncludeTermsAgreement')}>
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={orgLang === lang}>
                      <ArrayText
                        label="Consent Prompt"
                        name={`settings.ConsentText.${lang}`}
                      />
                    </Case>
                  ))}
                </Switch>
                <Switch>
                  {languagesSupported.map((orgLang) => (
                    <Case key={orgLang} condition={orgLang === lang}>
                      <VailTextField
                        shouldUnregister
                        control={form.control}
                        defaultValue="I agree"
                        label="Consent Button"
                        name={`settings.ConsentButtonText.${
                          orgLang as SupportedLocales
                        }`}
                      />
                    </Case>
                  ))}
                </Switch>
              </When>
            </Stack>
          </Stack>
        </VailAccordion>
      </Stack>
    </FormProvider>
  )
}

function ArrayText({ name, label }: { name: string; label: string }) {
  const confirm = useConfirm()

  const texArr = useFieldArray({
    name,
    rules: { required: false },
  })
  return (
    <>
      {texArr.fields.map((field, index) => (
        <Stack key={field.id} direction="row" gap={1}>
          <IconButton
            sx={{ alignSelf: 'flex-start' }}
            onClick={() =>
              confirm({
                content: `You are going to remove the text bubble ${objectToString(
                  field
                )}`,
                title: 'Are you sure you want to delete this text bubble?',
              }).then(() => texArr.remove(index))
            }
          >
            <Icon name="delete" />
          </IconButton>
          <VailTextField
            multiline
            label={label}
            maxRows={5}
            minRows={5}
            name={`${name}.${index}` as never}
          />
        </Stack>
      ))}
      <Button onClick={() => texArr.append('')}>
        Add text bubble for {label}
      </Button>
    </>
  )
}

function objectToString(obj: Record<string, string>) {
  const pairs = toPairs(obj)
  return pairs
    .filter(([key]) => /^\d+$/.test(key)) // filter out non-numeric keys
    .sort(([a], [b]) => Number(a) - Number(b)) // sort by numeric value
    .map((pairs) => pairs[1]) // get the value
    .join('') // join the values
}
